import { useId, useState, type ReactNode } from "react";
import {
    Box,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { UIMessage } from "ai";
import {
    ChatbotBenchmarkContextChunk,
    ChatbotReferenceMetadata,
} from "../../shared/models/chatbot-references.model";
import { formatSimilarityScore } from "../../shared/utils/similarity-score.utils";

type ChatbotRagBenchmarkProps = {
    message: UIMessage<ChatbotReferenceMetadata>;
    isLastAssistant: boolean;
    chatStatus: "ready" | "submitted" | "streaming" | "error";
    renderMessageActions: (benchmarkIcon: ReactNode) => ReactNode;
};

function BenchmarkTableRow({
    chunk,
    expanded,
    onToggle,
}: {
    chunk: ChatbotBenchmarkContextChunk;
    expanded: boolean;
    onToggle: () => void;
}) {
    return (
        <>
            <TableRow>
                <TableCell padding="checkbox">
                    <IconButton
                        size="small"
                        onClick={onToggle}
                        aria-label={expanded ? "Collapse chunk text" : "Expand chunk text"}
                    >
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>{chunk.path}</TableCell>
                <TableCell>{chunk.id}</TableCell>
                <TableCell>{formatSimilarityScore(chunk.score)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={4} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
                    <Collapse in={expanded} unmountOnExit>
                        <Box sx={{ py: 1, whiteSpace: "pre-wrap" }}>{chunk.text}</Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

/**
 * Admin-only RAG benchmark toggle and expandable chunk table for assistant messages.
 */
export function ChatbotRagBenchmark({
    message,
    isLastAssistant,
    chatStatus,
    renderMessageActions,
}: ChatbotRagBenchmarkProps): React.ReactElement {
    const [panelOpen, setPanelOpen] = useState(false);
    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
    const panelId = useId();

    const metadata = message.metadata;
    const chunks = metadata?.benchmark?.selectedContextChunks;
    const inFlight = isLastAssistant && (chatStatus === "streaming" || chatStatus === "submitted");
    const hasBenchmark = Boolean(metadata && !inFlight && chunks && chunks.length > 0);

    const togglePanel = () => {
        setPanelOpen((open) => {
            if (open) {
                setExpandedRowIds(new Set());
            }
            return !open;
        });
    };

    const toggleRow = (id: string) => {
        setExpandedRowIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const benchmarkIcon = hasBenchmark ? (
        <Tooltip title="RAG benchmark">
            <IconButton
                size="small"
                aria-label="Toggle RAG benchmark panel"
                aria-expanded={panelOpen}
                aria-controls={panelId}
                onClick={togglePanel}
            >
                <AssessmentOutlined fontSize="small" />
            </IconButton>
        </Tooltip>
    ) : null;

    return (
        <>
            {renderMessageActions(benchmarkIcon)}
            {hasBenchmark && (
                <Collapse in={panelOpen} unmountOnExit>
                    <Box
                        id={panelId}
                        role="region"
                        aria-label="RAG context chunks"
                        sx={{ mt: 0.5, mb: 0.5 }}
                    >
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Path</TableCell>
                                        <TableCell>Chunk ID</TableCell>
                                        <TableCell>Similarity score</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {chunks!.map((chunk) => (
                                        <BenchmarkTableRow
                                            key={chunk.id}
                                            chunk={chunk}
                                            expanded={expandedRowIds.has(chunk.id)}
                                            onToggle={() => toggleRow(chunk.id)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Collapse>
            )}
        </>
    );
}
